import { MobilePlatform } from './platformUtil';
import {
  ANDROID_PACKAGE_NAME,
  APP_DEEP_LINK_PATH,
  APP_DEEP_LINK_SCHEME,
  PLAY_STORE_URL,
} from '../constants/mobileLandingConstants';

/** Builds the iOS custom-scheme URL, e.g. `com.trucktrax.geotrax://open`. */
export const buildSchemeUrl = (path: string): string => `${APP_DEEP_LINK_SCHEME}://${path}`;

/**
 * Builds an Android `intent://` URL. Android opens the app if the scheme/package is installed,
 * and natively navigates to `browser_fallback_url` (the Play Store) if it is not — no JS timer
 * needed on Android.
 */
export const buildAndroidIntentUrl = (path: string, fallbackUrl: string): string =>
  `intent://${path}#Intent;scheme=${APP_DEEP_LINK_SCHEME};package=${ANDROID_PACKAGE_NAME};` +
  `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;

export interface AppLauncherDeps {
  navigate?: (url: string) => void;
}

/**
 * Attempts to open the TruckTrax Geo app.
 *
 * - Android: a single `intent://` navigation; Android opens the app if installed and natively
 *   falls back to the Play Store if not — no dialog, no JS timer.
 * - iOS: just opens the custom scheme. The landing page shows a dedicated "Get the app" App Store
 *   button alongside this, so there is intentionally NO JS store fallback here — a fallback would
 *   trigger a second Safari "address is invalid" dialog when the app isn't installed.
 * - other: no-op — there is no installed app to open on desktop.
 *
 * `navigate` is injectable so the behavior is unit-testable.
 */
export const openGeotraxApp = (platform: MobilePlatform, deps: AppLauncherDeps = {}): void => {
  const {
    navigate = url => {
      window.location.href = url;
    },
  } = deps;

  if (platform === 'android') {
    navigate(buildAndroidIntentUrl(APP_DEEP_LINK_PATH, PLAY_STORE_URL));
    return;
  }

  if (platform === 'ios') {
    navigate(buildSchemeUrl(APP_DEEP_LINK_PATH));
  }
};
